import { useAuth } from "context/AuthContext";
import { FC, useEffect, useState } from "react";
import {
  CrimeReference,
  FurtherInvestigation,
  InvestigationFile,
  Roles,
} from "types";
import { errorHandler, errorHandlerOnForm, hasRole } from "utils/helper";
import FurtherInvestigationForm from "../modal/FurtherInvestigationForm";
import { useNavigate } from "react-router-dom";
import {
  addFurtherInvestigationFeedback,
  editFurtherInvestigation2,
  fetchReceivingInvestigationFileById,
} from "services/prosecutorPoliceService";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import { LoadingIcon } from "utils/constant";

type Props = {
  furtherInvestigation: FurtherInvestigation;
  incident?: CrimeReference | null;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const FurtherInvestigationFileDecision: FC<Props> = ({
  furtherInvestigation,
  incident,
  setRefetch,
}) => {
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isClerk = hasRole(loginData?.roles, [Roles.clerk]);
  const [file, setFile] = useState<InvestigationFile>();
  const [loading, setLoading] = useState(true);

  const fetchData = (id: number) => {
    fetchReceivingInvestigationFileById(id)
      .then((data) => {
        setFile(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    fetchData(furtherInvestigation?.investigation_file as number);
  }, [incident]);

  const [openForm, setOpenForm] = useState(false);
  const [action, setAction] = useState<"Add" | "Forward" | "Reject" | "Edit">(
    "Add"
  );

  // Inline respond-back (replaces the reject modal).
  const [showRespondBack, setShowRespondBack] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [respondLoading, setRespondLoading] = useState(false);

  const submitRespondBack = () => {
    if (!feedback.trim()) {
      showTooltip(
        t("feedback_required", { defaultValue: "Feedback is required." }),
        "error",
      );
      return;
    }
    setRespondLoading(true);
    // Minimal JSON PATCH — only the decision changes; avoids the multipart
    // suspects_ids "expected a list" error and needs no file.
    editFurtherInvestigation2({
      id: furtherInvestigation?.id,
      decision: "revice",
    })
      .then(() => {
        addFurtherInvestigationFeedback({
          feedback_text: feedback,
          created_by: loginData?.full_name,
          investigation_request: furtherInvestigation?.id ?? null,
        });
        showTooltip(t("successfully_revised"), "success");
        setShowRespondBack(false);
        setFeedback("");
        setRefetch((prev) => !prev);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail ||
            errorHandlerOnForm(error) ||
            errorHandler(error),
          "error",
        );
      })
      .finally(() => setRespondLoading(false));
  };

  return (
    <div className="space-y-2">
      {isProsecutor ? (
        furtherInvestigation ? (
          <button
            className="border border-[#212121] bg-white px-8 py-2 w-full mt-3 text-[#212121] font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-gray-100 transition"
            onClick={() => {
              setOpenForm(true);
              setAction("Edit");
            }}
          >
            Edit Further Investigation
          </button>
        ) : (
          <button
            className="border border-[#212121] bg-white px-8 py-2 w-full mt-3 text-[#212121] font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-gray-100 transition"
            onClick={() => {
              setOpenForm(true);
              setAction("Add");
            }}
          >
            Add Further Investigation
          </button>
        )
      ) : furtherInvestigation ? (
        <div className="flex flex-row gap-3">
          {isClerk ? (
            <div className="flex flex-col gap-5">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="bg-red-600 text-white px-4 py-2 inline-block rounded-b-md mb-4">
                  Incident
                </div>

                <div className="flex gap-3 items-center mb-4">
                  {/* <div className="">{CheckIcon}</div> */}
                  <p className="text-gray-600">
                    Manage incident details and registration for this
                    investigation file.
                  </p>
                </div>
                <button
                  className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                  onClick={() => {
                    navigate(
                      incident
                        ? "/record-incident/" + incident?.uu_id
                        : "/incident-register",
                      {
                        state: { file, incident },
                      }
                    );
                  }}
                >
                  {loading
                    ? "Loading..."
                    : incident
                    ? "Record Incident"
                    : "Register Incident"}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="inline-flex items-center justify-center gap-2 w-full mt-3 px-8 py-2.5 bg-emerald-600 text-white font-[Poppins] font-[500] text-base rounded-lg shadow-sm hover:bg-emerald-700 transition-colors cursor-pointer"
              onClick={() => {
                setOpenForm(true);
                setAction("Forward");
              }}
            >
              {furtherInvestigation?.decision === "forward_to_police"
                ? t("add_result", { defaultValue: "Add Result" })
                : furtherInvestigation?.decision === "forward_to_coordinator"
                ? t("approve_and_forward_to_registrar", {
                    defaultValue: "Approve & Forward to Registrar",
                  })
                : furtherInvestigation?.decision === "forward_to_registrar"
                ? t("add_registration_number", {
                    defaultValue: "Add Registration Number",
                  })
                : furtherInvestigation?.decision === "back_coordinator"
                ? t("forward_to_clerk", { defaultValue: "Forward to Clerk" })
                : t("approve", { defaultValue: "Approve" })}
            </button>
          )}
          {isCoordinator &&
            furtherInvestigation?.decision === "forward_to_coordinator" && (
              <button
                className="border border-amber-300 bg-amber-50 px-8 py-2 w-full mt-3 text-amber-700 font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-amber-100 transition"
                onClick={() => setShowRespondBack((v) => !v)}
              >
                ↩️ {t("respond_back")}
              </button>
            )}
        </div>
      ) : null}

      {/* Inline respond-back feedback (no modal) */}
      {showRespondBack && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            ↩️ {t("respond_back")}
          </label>
          <textarea
            rows={4}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-y"
            placeholder={t("write_your_feedback_here", {
              defaultValue: "Write your feedback here…",
            })}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => {
                setShowRespondBack(false);
                setFeedback("");
              }}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              disabled={respondLoading || !feedback.trim()}
              onClick={submitRespondBack}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {respondLoading ? (
                <>
                  {LoadingIcon}
                  {t("submitting")}
                </>
              ) : (
                t("submit_response")
              )}
            </button>
          </div>
        </div>
      )}

      {openForm && (
        <FurtherInvestigationForm
          isOpen={openForm}
          action={action}
          file={undefined}
          furtherInvestigation={furtherInvestigation}
          incident={incident}
          onClose={(refetch) => {
            setOpenForm(false);
            refetch && setRefetch((prev) => !prev);
          }}
        />
      )}
    </div>
  );
};

export default FurtherInvestigationFileDecision;
